import * as React from 'react';
import {Box, Divider, List, ListItemButton, ListItemIcon, ListItemText, Typography} from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {useTranslation} from 'react-i18next';
import type {AttentionItem} from '../../hooks/useDashboard';

interface AttentionListProps {
    readonly items: AttentionItem[];
}

const ICONS: Record<AttentionItem['icon'], React.ElementType> = {
    evidence: AccessTimeOutlinedIcon,
    contracts: DescriptionOutlinedIcon,
    payChanges: InfoOutlinedIcon,
};

const TONE_COLORS: Record<AttentionItem['tone'], string> = {
    error: 'error.main',
    warning: 'warning.main',
    info: 'info.main',
};

export default function AttentionList({items}: Readonly<AttentionListProps>): React.ReactElement {
    const {t} = useTranslation();

    return (
        <>
            <Typography gutterBottom sx={{color: 'text.primary', fontSize: 18, fontWeight: 600, textAlign: 'left'}}>
                {t('dashboard.attention.title')}
            </Typography>
            <Divider/>
            {items.length === 0 ? (
                <Box sx={{py: 4, textAlign: 'center'}}>
                    <Typography variant="body2" sx={{color: 'text.secondary'}}>
                        {t('dashboard.attention.empty')}
                    </Typography>
                </Box>
            ) : (
                <List disablePadding>
                    {items.map((item) => {
                        const Icon = ICONS[item.icon];
                        return (
                            <ListItemButton
                                key={item.id}
                                onClick={item.onClick}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    mt: 1.5,
                                }}
                            >
                                <ListItemIcon sx={{color: TONE_COLORS[item.tone], minWidth: 40}}>
                                    <Icon/>
                                </ListItemIcon>
                                <ListItemText
                                    primaryTypographyProps={{sx: {color: TONE_COLORS[item.tone], fontWeight: 600}}}
                                    secondaryTypographyProps={{sx: {color: 'text.secondary'}}}
                                    primary={item.title}
                                    secondary={item.description}
                                />
                                <ChevronRightIcon sx={{color: 'text.disabled'}}/>
                            </ListItemButton>
                        );
                    })}
                </List>
            )}
        </>
    );
}
